import React, { useEffect, useState, useMemo } from "react";
import { FaChartBar } from "react-icons/fa";
import { Header, DateRangePicker } from "../../../components/ui";
import PageLayout from "../../../components/layout/PageLayout";
import LogistSummaryPanel from "../../../components/ui/LogistSummaryPanel";
import api from "../../../services/api";
import AnalyticsFilterPanel from "../../../components/ui/components/AnalyticsFilterPanel";

// Компоненты аналитики
import AnalyticsSummary from "./components/AnalyticsSummary";
import DeliveryTrendsChart from "./components/DeliveryTrendsChart";
import DriversRatingTable from "./components/DriversRatingTable";
import RoutesEfficiencyChart from "./components/RoutesEfficiencyChart";
import EmployeesEfficiencyChart from "./components/EmployeesEfficiencyChart";
import DeliveryFailuresChart from "./components/DeliveryFailuresChart";
import QualityStats from "./components/QualityStats";
import CourierActivity from "./components/CourierActivity";

export default function Analytics() {
  const [range, setRange] = useState({ from: "", to: "" });
  const [routes, setRoutes] = useState([]);
  const [filters, setFilters] = useState({
    routeNumber: null,
    driver: null,
    loader: null,
    district: null,
    status: null,
    paymentType: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // === Загрузка данных ===
  useEffect(() => {
    if (!range.from || !range.to) return;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get("/routes/by-date", {
          params: { startDate: range.from, endDate: range.to },
        });
        setRoutes(Array.isArray(res) ? res : []);
      } catch (err) {
        console.error("Ошибка API:", err);
        setError("Ошибка загрузки данных");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [range]);

  // === Фильтрация маршрутов ===
  const filteredRoutes = useMemo(() => {
    return routes.filter((r) => {
      if (filters.routeNumber && r.route_number !== filters.routeNumber) return false;
      if (filters.driver && r.driver !== filters.driver) return false;
      if (
        filters.loader &&
        ![r.loader1, r.loader2].filter(Boolean).includes(filters.loader)
      )
        return false;
      if (filters.district && !r.route_districts?.includes(filters.district))
        return false;
      return true;
    });
  }, [routes, filters]);

  // === Все квитанции ===
  const allReceipts = filteredRoutes.flatMap((r) => r.receipts || []);

  const filteredReceipts = useMemo(() => {
    return allReceipts.filter((r) => {
      if (filters.status && r.status !== filters.status) return false;
      if (filters.paymentType && r.payment_type !== filters.paymentType) return false;
      return true;
    });
  }, [allReceipts, filters]);

  // === Метрики ===
  const totalRoutes = filteredRoutes.length;
  const totalReceipts = filteredReceipts.length;

  const delivered = filteredReceipts.filter((r) => r.status === "Закрыта").length;
  const notDelivered = filteredReceipts.filter((r) => r.status === "Не доставлено").length;
  const open = filteredReceipts.filter((r) => r.status === "Открыта").length;

  const totalAmount = filteredReceipts.reduce(
    (sum, r) => sum + (parseFloat(r.amount) || 0),
    0
  );

  const avgTimeMs =
    filteredReceipts.reduce((sum, r) => {
      if (!r.status_changed_at || !r.time) return sum;
      const diff = new Date(r.status_changed_at) - new Date(r.time);
      return sum + (isNaN(diff) ? 0 : diff);
    }, 0) / (delivered || 1);

  const avgTime =
    avgTimeMs > 0
      ? `${Math.floor(avgTimeMs / 3600000)} ч ${Math.floor(
          (avgTimeMs % 3600000) / 60000
        )} мин`
      : "—";

  // === Рейтинг водителей ===
  const byDriver = {};
  for (const route of filteredRoutes) {
    const name = route.driver || "Без водителя";
    const receipts = route.receipts || [];
    const total = receipts.length;
    const done = receipts.filter((r) => r.status === "Закрыта").length;
    if (!byDriver[name]) byDriver[name] = { total: 0, done: 0 };
    byDriver[name].total += total;
    byDriver[name].done += done;
    byDriver[name].percent = Math.round(
      (byDriver[name].done / Math.max(1, byDriver[name].total)) * 100
    );
  }

  const driversRating = Object.entries(byDriver).map(([driver, val]) => ({
    driver,
    ...val,
  }));

  const topDriver =
    driversRating
      .slice()
      .sort((a, b) => b.done - a.done || b.percent - a.percent)[0]?.driver || "—";

  // === Причины недоставок ===
  const byReason = filteredReceipts
    .filter((r) => r.status === "Не доставлено" && r.reason)
    .reduce((acc, r) => {
      acc[r.reason] = (acc[r.reason] || 0) + 1;
      return acc;
    }, {});

  // === Данные для панели ===
  const summaryData = {
    routesTotal: totalRoutes,
    receiptsTotal: totalReceipts,
    delivered,
    delayed: notDelivered,
    open, // ✅ теперь диаграмма корректно отображает открытые
    avgDeliveryTime: avgTime,
    totalAmount: `${totalAmount.toLocaleString("ru-RU")} ₽`,
    activeDrivers: new Set(filteredRoutes.map((r) => r.driver).filter(Boolean)).size,
    topDriver,
  };

  return (
    <PageLayout
      header={
        <Header
          icon={FaChartBar}
          title="Аналитика логистики"
          subtitle="Сводные показатели и эффективность маршрутов за выбранный период"
        />
      }
      controls={<DateRangePicker mode="range" onChange={setRange} />}
      stats={<LogistSummaryPanel data={summaryData} />}
    >
      {/* Панель фильтров */}
      <AnalyticsFilterPanel
        routes={routes}
        filters={filters}
        setFilters={setFilters}
      />

      {/* Состояния */}
      {loading && <p>Загрузка...</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}

      {/* Основное содержимое */}
      {!loading && !error && totalReceipts > 0 && (
        <>
          <section style={{ marginBottom: 24 }}>
            <AnalyticsSummary
              totalDeliveries={totalReceipts}
              delivered={delivered}
              notDelivered={notDelivered}
              avgTime={avgTime}
              totalAmount={totalAmount}
            />
          </section>

          <section
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
              gap: 20,
              marginBottom: 32,
            }}
          >
            <DeliveryTrendsChart routes={filteredRoutes} />
            <EmployeesEfficiencyChart routes={filteredRoutes} />
            <RoutesEfficiencyChart routes={filteredRoutes} />
          </section>

          <section
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))",
              gap: 20,
            }}
          >
            <DriversRatingTable data={driversRating} />
            <DeliveryFailuresChart data={byReason} />
            <QualityStats receipts={filteredReceipts} />
            <CourierActivity />
          </section>
        </>
      )}

      {!loading && !error && totalReceipts === 0 && (
        <p>Нет данных за выбранный период или по фильтрам.</p>
      )}
    </PageLayout>
  );
}
